import React from 'react';
import PathCell from './PathCell';
import { useBoardState, BoardGameState } from '../../hooks/useBoardState';
import { useBoardInteractions } from '../../hooks/useBoardInteractions';

interface BoardArmProps {
  type: 'up' | 'left' | 'right' | 'down';
  gameState: BoardGameState;
  validMoves: number[];
  onSquareClick: (position: number) => void;
  onPieceClick?: (pieceIndex: number) => void;
}

const BoardArm: React.FC<BoardArmProps> = ({
  type,
  gameState,
  validMoves,
  onSquareClick,
  onPieceClick,
}) => {
  const {
    getPiecesAtPosition,
    isSafePosition,
    isHomeEntryPosition,
    shouldHighlightPiece,
    getPositionColorClass,
  } = useBoardState(gameState, validMoves);

  const { handleCellClick, handleBoardPieceClick } = useBoardInteractions({
    gameState,
    onSquareClick,
    onPieceClick,
  });

  const renderCell = (
    colorClass: string = '',
    position: number = -1,
    isHomeStretch: boolean = false,
    homeStretchColor: string = ''
  ) => {
    const piecesOnPosition = getPiecesAtPosition(
      position,
      isHomeStretch,
      homeStretchColor
    );
    const isSafe = isSafePosition(position, isHomeStretch);
    const isHomeEntry = isHomeEntryPosition(position);
    const finalColorClass = getPositionColorClass(
      position,
      isHomeStretch,
      colorClass
    );

    // Filter valid moves to highlight pieces correctly
    const highlightedPieces = piecesOnPosition
      .filter((pieceInfo) =>
        shouldHighlightPiece(pieceInfo.player.id, pieceInfo.pieceIndex)
      )
      .map((pieceInfo) => pieceInfo.pieceIndex);

    return (
      <PathCell
        key={`${position}-${isHomeStretch ? homeStretchColor : 'main'}`}
        position={position}
        colorClass={finalColorClass}
        isHomeStretch={isHomeStretch}
        homeStretchColor={homeStretchColor}
        isSafe={isSafe}
        isHomeEntry={isHomeEntry}
        piecesOnPosition={piecesOnPosition}
        validMoves={highlightedPieces}
        onClick={() => handleCellClick(position, piecesOnPosition)}
        onPieceClick={handleBoardPieceClick}
      />
    );
  };

  const renderUpArm = () => (
    <div className="cell-container">
      <div className="row">
        {renderCell('', 11)}
        {renderCell('', 12)}
        {renderCell('', 13)}
      </div>
      <div className="row">
        {renderCell('', 10)}
        {renderCell('green', 100, true, 'green')}
        {renderCell('', 14)}
      </div>
      <div className="row">
        {renderCell('', 9)}
        {renderCell('green', 101, true, 'green')}
        {renderCell('', 15)}
      </div>
      <div className="row">
        {renderCell('', 8)}
        {renderCell('green', 102, true, 'green')}
        {renderCell('', 16)}
      </div>
      <div className="row">
        {renderCell('', 7)}
        {renderCell('green', 103, true, 'green')}
        {renderCell('', 17)}
      </div>
      <div className="row">
        {renderCell('', 6)}
        {renderCell('green', 104, true, 'green')}
        {renderCell('', 18)}
      </div>
    </div>
  );

  const renderLeftArm = () => (
    <div className="cell-container">
      <div className="row">
        {renderCell('', 0)}
        {renderCell('', 1)}
        {renderCell('', 2)}
        {renderCell('', 3)}
        {renderCell('', 4)}
        {renderCell('', 5)}
      </div>
      <div className="row">
        {renderCell('', 51)}
        {renderCell('red', 100, true, 'red')}
        {renderCell('red', 101, true, 'red')}
        {renderCell('red', 102, true, 'red')}
        {renderCell('red', 103, true, 'red')}
        {renderCell('red', 104, true, 'red')}
      </div>
      <div className="row">
        {renderCell('', 50)}
        {renderCell('', 49)}
        {renderCell('', 48)}
        {renderCell('', 47)}
        {renderCell('', 46)}
        {renderCell('', 45)}
      </div>
    </div>
  );

  const renderRightArm = () => (
    <div className="cell-container">
      <div className="row">
        {renderCell('', 19)}
        {renderCell('', 20)}
        {renderCell('', 21)}
        {renderCell('', 22)}
        {renderCell('', 23)}
        {renderCell('', 24)}
      </div>
      <div className="row">
        {renderCell('yellow', 104, true, 'yellow')}
        {renderCell('yellow', 103, true, 'yellow')}
        {renderCell('yellow', 102, true, 'yellow')}
        {renderCell('yellow', 101, true, 'yellow')}
        {renderCell('yellow', 100, true, 'yellow')}
        {renderCell('', 25)}
      </div>
      <div className="row">
        {renderCell('', 31)}
        {renderCell('', 30)}
        {renderCell('', 29)}
        {renderCell('', 28)}
        {renderCell('', 27)}
        {renderCell('', 26)}
      </div>
    </div>
  );

  const renderDownArm = () => (
    <div className="cell-container">
      <div className="row">
        {renderCell('', 44)}
        {renderCell('blue', 104, true, 'blue')}
        {renderCell('', 32)}
      </div>
      <div className="row">
        {renderCell('', 43)}
        {renderCell('blue', 103, true, 'blue')}
        {renderCell('', 33)}
      </div>
      <div className="row">
        {renderCell('', 42)}
        {renderCell('blue', 102, true, 'blue')}
        {renderCell('', 34)}
      </div>
      <div className="row">
        {renderCell('', 41)}
        {renderCell('blue', 101, true, 'blue')}
        {renderCell('', 35)}
      </div>
      <div className="row">
        {renderCell('', 40)}
        {renderCell('blue', 100, true, 'blue')}
        {renderCell('', 36)}
      </div>
      <div className="row">
        {renderCell('', 39)}
        {renderCell('', 38)}
        {renderCell('', 37)}
      </div>
    </div>
  );

  switch (type) {
    case 'up':
      return renderUpArm();
    case 'left':
      return renderLeftArm();
    case 'right':
      return renderRightArm();
    case 'down':
      return renderDownArm();
    default:
      return null;
  }
};

export default BoardArm;
